// Consultation/ConsultationConference.tsx
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import CircularProgress from '../Core/components/elements/CircularProgress';
import {FC, FCCC, FCSC, FR} from 'WideLayout/Layouts';
import {axios} from 'Auth/axiosConfig';
import {IConsultation} from '../../Types/psychology/consultation';
import pprint from '../../Utils/pprint';
import RoomWith from 'Chat/RoomWith';
import {IUser} from "../../Types/core/user";
import UserAvatar from "User/UserAvatar";
import {RoomsProvider} from "Chat/RoomsContext";
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Импортируем хук для изменения maxWidth:
import {useCabinetWidth} from 'Cabinet/Cabinet';
import {IconButton, useMediaQuery} from "@mui/material";
import ChatIcon from '@mui/icons-material/Chat';

// Импортируем ваш модальный компонент
import Modal from 'Core/components/elements/Modal/Modal';
import {useTheme} from "Theme/ThemeContext";
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import {headerHeight} from "Core/components/Header/Header";
import SocialButtons from "Modules/SocialButtons";
import Container from "WideLayout/Container";
import DailyRoom from "DailyCo/DailyRoom";
import LiveDigitalRoom from 'Modules/LiveDigital/LiveDigitalRoom';

const ConsultationConference: React.FC = () => {
    const {consultationId} = useParams<{ consultationId: string }>();
    const {byResponse} = useErrorProcessing();
    const [consultation, setConsultation] = useState<IConsultation | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
    const {user} = useContext(AuthContext) as AuthContextType;
    // Управление видимостью модалки чата
    const [isChatModalOpen, setIsChatModalOpen] = useState(false);
    const {plt} = useTheme();
    // Медиа-запросы
    const isGt1100 = useMediaQuery('(min-width: 1100px)');
    const isGt700 = useMediaQuery('(min-width: 700px)');
    const roomHeader = useRef<HTMLDivElement>(null);
    const [roomHeaderHeight, setRoomHeaderHeight] = useState<number>(0);

    // Меняем ширину Cabinet
    const {setCabinetMaxWidth} = useCabinetWidth();

    // Подтягиваем данные консультации
    useEffect(() => {
        if (roomHeader.current) {
            setRoomHeaderHeight(roomHeader.current.getBoundingClientRect().height);
        }
        if (!consultationId) return;

        axios
            .get(`api/v1/consultations/${consultationId}/?tier=3`)
            .then((r) => {
                pprint('Consultation with conference', r.data);
                setConsultation(r.data);
                // Выбираем первого психолога по умолчанию
                if (r.data.psychologists.length > 0) {
                    setSelectedUserId(r.data.psychologists[0].id);
                }
            })
            .catch((e) => byResponse(e));
    }, [consultationId]);

    // Ставим другую ширину на время конференции
    useEffect(() => {
        setCabinetMaxWidth('1800px');
        return () => {
            // Возвращаем дефолт
            setCabinetMaxWidth('1220px');
        };
    }, [setCabinetMaxWidth]);

    if (!consultation || !user) {
        return (
            <FCCC h="100%">
                <CircularProgress size="180px"/>
            </FCCC>
        );
    }

    // Список всех участников (психологи + клиенты)
    const allParticipants: IUser[] = [
        ...(consultation.psychologists || []),
        ...(consultation.clients || []),
    ];

    return (
        <RoomsProvider>
            <FR scroll="auto" cls="conference" h="100%"
                flexWrap={isGt1100 ? 'unset' : 'wrap'}
                pos={'relative'} maxH={`calc(100vh - ${headerHeight}px)`}>
                {/* Если ширина < 1100px, показываем плавающую кнопку */}
                {!isGt1100 && (
                    <IconButton
                        onClick={() => setIsChatModalOpen(true)}
                        style={{
                            position: 'fixed',
                            bottom: '10rem',
                            right: 16,
                            zIndex: 19,
                            backgroundColor: plt.text.contrast + '43',
                            color: plt.text.primary90,
                        }}>
                        <ChatIcon/>
                    </IconButton>
                )}
                {/* Левая часть (или основная) — комната + подсказки */}
                <FC grow h={isGt1100 ? '100%' : '110%'}>
                    {consultation.conference?.name && (
                        <FC h="100%">
                            {consultation.conference.polymorphic_name === 'LiveDigital room' ? (
                                <LiveDigitalRoom consultation={consultation}/>
                            ) : (
                                <DailyRoom roomName={consultation.conference.name}/>
                            )}
                        </FC>
                    )}
                    <Container p={'3px'} sx={{
                        display: 'flex',
                        flexDirection: isGt700 ? 'row' : 'column',
                    }}>
                        <FC fontSize={'.8rem'} w={'100%'} maxW={460}>
                            <span className="lh-1">
                                Если вы тут впервые, введите свое имя,
                                подключите микрофон и камеру. Если возникли проблемы, попробуйте:
                            </span>
                            <span style={{lineHeight: '1rem'}}>1) Отключите VPN, если он включен</span>
                            <span style={{lineHeight: '1rem'}}>
                                2)&nbsp;<a href={''} onClick={() => window.location.reload()}>Обновить страницу</a>
                            </span>
                            <span style={{lineHeight: '1rem'}}>
                                3) Проверьте&nbsp;<a href={'https://yandex.ru/internet/'}>скорость интернета</a>
                                <Tooltip
                                    title={<div style={{fontSize: '12px', lineHeight: '1.4', padding: '4px'}}>
                                        Рекомендуемая скорость подключения к интернету составляет не менее 8 Мбит/с
                                        (исходящая/входящая).
                                    </div>}
                                    placement="top" arrow>
                                    <HelpOutlineIcon style={{fontSize: '1rem'}} sx={{cursor: 'pointer', ml: 1}}/>
                                </Tooltip>
                            </span>
                            <span style={{lineHeight: '1rem'}}>4) Перейти во вкладку&nbsp;<a href={'/consultations'}>Моё
                                расписание</a>&nbsp;и
                                зайти в сеанс повторно</span>
                        </FC>

                        <Container px={'3px'} pb={'3px'} sx={{
                            display: 'flex',
                            flexDirection: isGt700 ? 'column' : 'row',
                        }}>
                            <FR lh={'1rem'} cls={'fs-7'}>Не удалось решить проблему? Напишите нам!</FR>
                            <SocialButtons
                                exclude={['Vk', 'Odnoklassniki', 'YandexZen', 'TelegramSocial']}
                                rounded={4} bg={plt.bg.contrast05} px={1} iconStyle={{
                                transform: 'scale(.7)',
                            }}/>
                        </Container>
                    </Container>
                </FC>

                {/* Правая часть: Участники и чат (только если >=1100px) */}
                {isGt1100 && (
                    <FC maxH={`calc(100vh - ${headerHeight}px)`} pl={1} pr={0} mt={0}>
                        <FR ref={roomHeader} g={1} pt={2}>
                            {allParticipants.map((participant) => (
                                <FCSC cls={'ftrans-300-eio'} mb={0} key={participant.id} sx={{
                                    transform: selectedUserId == participant.id ? 'scale(1.05)' : ''
                                }}>
                                    <UserAvatar
                                        size="3rem"
                                        avatar={participant.avatar}
                                        sx={{cursor: 'pointer'}}
                                        onClick={() => setSelectedUserId(participant.id)}
                                    />
                                    {participant.id === user.id
                                        ? <FCSC cls={'fs-7'} lh={'1.2rem'}>
                                            <span className={'ftrans-300-eio'} style={{
                                                color: selectedUserId == participant.id
                                                    ? plt.colors.secondary.colorful
                                                    : ''
                                            }}>Избранное</span>
                                        </FCSC>
                                        : <FCSC cls={'fs-7'}
                                                color={selectedUserId == participant.id
                                                    ? plt.colors.secondary.colorful
                                                    : ''}
                                                lh={'1.05rem'} mt={'2px'}>
                                            <span className={'ftrans-300-eio'}>{participant.first_name}</span>
                                            <span className={'ftrans-300-eio'}>{participant.last_name}</span>
                                        </FCSC>}
                                </FCSC>
                            ))}
                        </FR>
                        <FC h={'100%'} maxW={370} maxH={`calc(87vh - ${headerHeight + roomHeaderHeight}px)`}>
                            {selectedUserId && <RoomWith userId={selectedUserId}/>}
                        </FC>
                    </FC>
                )}
                {!isGt1100 && (
                    <Modal
                        isOpen={isChatModalOpen}
                        onClose={() => setIsChatModalOpen(false)}
                        title="Чат" closeBtn
                        cls={'modal-room-base-wrapper h-100 fc'}
                        sxContent={{maxWidth: 360, p: 0}}
                    >
                        <FC h={'100%'} maxW={350}>
                            <FR g={1} flexWrap="wrap">
                                {allParticipants.map((participant) => (
                                    <FCSC mb={0} key={participant.id}>
                                        <UserAvatar
                                            size="3rem"
                                            avatar={participant.avatar}
                                            sx={{cursor: 'pointer'}}
                                            onClick={() => setSelectedUserId(participant.id)}
                                        />
                                        {participant.id === user.id
                                            ? <FCSC cls={'fs-7'} lh={'1.2rem'}>
                                                <span>Избранное</span>
                                            </FCSC>
                                            : <FCSC cls={'fs-7'} lh={'1.2rem'}>
                                                <span>{participant.first_name}</span>
                                                <span>{participant.last_name}</span>
                                            </FCSC>}
                                    </FCSC>
                                ))}
                            </FR>
                            <FC h={'100%'} maxH={`calc(89vh - ${headerHeight + roomHeaderHeight}px)`}>
                                {selectedUserId && <RoomWith showHeader={false} userId={selectedUserId}/>}
                            </FC>
                        </FC>
                    </Modal>
                )}
            </FR>
        </RoomsProvider>
    );
};

export default ConsultationConference;
